import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Plus, Edit, Trash2, Mail, UserCircle, AlertTriangle, Send, Loader2, Shield, Headphones, MessageSquare } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { useTheme } from "../../contexts/ThemeContext";
import { usePlanLimits } from "../../contexts/PlanLimitContext";
import LimitWarningModal from "../../components/LimitWarningModal";

const TeamPage = () => {
  const { isDark } = useTheme();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(null);
  
  // Plan limits
  const { limits, canAddTeamMember, isNearLimit, refetch: refetchLimits, isSmsReadyAsync, hasActivePhone } = usePlanLimits();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "dispatcher"
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/team`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setMembers(response.data);
    } catch (error) {
      // Only show error if it's not an auth error (handled globally)
      if (error.response?.status !== 401) {
        console.error("Failed to fetch team:", error);
        toast.error("Failed to load team members");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      };

      // Prepare data with empty specialties since team members don't need them
      const submitData = {
        ...formData,
        specialties: []
      };

      if (editingMember) {
        await axios.put(`${API}/team/${editingMember.member_id}`, submitData, config);
        toast.success("Team member updated");
      } else {
        const res = await axios.post(`${API}/team`, submitData, config);
        if (res.data?.invite_email_sent) {
          toast.success(`Team member added — invite email sent to ${submitData.email}`);
        } else if (submitData.email) {
          toast.warning(
            `Team member added, but the invite email could not be sent${res.data?.invite_email_error ? `: ${res.data.invite_email_error}` : ""}. Use the mail icon on their card to resend.`,
            { duration: 8000 }
          );
        } else {
          toast.success("Team member added");
        }
      }

      setDialogOpen(false);
      resetForm();
      fetchMembers();
      refetchLimits();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save team member");
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this team member?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/team/${memberId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Team member removed");
      fetchMembers();
      refetchLimits();
    } catch (error) {
      toast.error("Failed to remove team member");
    }
  };

  const handleSendInvite = async (member, method = "email") => {
    if (method === "email" && !member.email) {
      toast.error("Team member needs an email address to receive email invite");
      return;
    }
    if (method === "sms") {
      if (!member.phone) {
        toast.error("Team member needs a phone number to receive SMS invite");
        return;
      }
      if (!(await isSmsReadyAsync())) return;
    }
    
    setSendingInvite(member.member_id);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/team/${member.member_id}/invite`, {
        send_email: method === "email",
        send_sms: method === "sms"
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = res.data || {};
      if (method === "email" && !data.email_sent) {
        toast.error(data.email_error || data.message || "Invite email could not be sent. Please try again.");
      } else if (method === "sms" && !data.sms_sent) {
        toast.error(data.message || "Invite SMS could not be sent. Please try again.");
      } else {
        toast.success(method === "email"
          ? `Email invite sent to ${member.email}!`
          : `SMS invite sent to ${member.phone}!`);
      }
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSendingInvite(null);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      email: member.email,
      phone: member.phone || "",
      role: member.role
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingMember(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      role: "dispatcher"
    });
  };

  const getRoleBadge = (role) => {
    const styles = {
      dispatcher: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      admin: "bg-[#FFCC00]/20 text-[#FFCC00] border-[#FFCC00]/30"
    };
    return styles[role] || styles.dispatcher;
  };

  const getRoleIcon = (role) => {
    if (role === "admin") return <Shield className="w-3 h-3" />;
    return <Headphones className="w-3 h-3" />;
  };

  // Handle add button click
  const handleAddClick = () => {
    if (!canAddTeamMember()) {
      setShowLimitModal(true);
    } else {
      setDialogOpen(true);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "active") return "bg-green-500/20 text-green-400 border-green-500/30";
    if (status === "invited") return "bg-blue-500/20 text-blue-400 border-blue-500/30";
    return "bg-gray-500/20 text-gray-400 border-gray-500/30";
  };

  // Get team member limits info
  const teamLimits = limits?.limits?.team_members || { used: 0, limit: 2, unlimited: false };
  const atLimit = !canAddTeamMember();
  const nearLimit = isNearLimit("team_members");

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Limit Warning Modal */}
      <LimitWarningModal
        open={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        title="Team Member Limit Reached"
        limitType="team_members"
        current={teamLimits.used}
        limit={teamLimits.limit}
        planName={limits?.plan || "Current"}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-foreground font-['Manrope']">Team</h2>
            {!teamLimits.unlimited && (
              <Badge className={`${nearLimit ? 'bg-yellow-500/20 text-yellow-400' : 'bg-theme-hover text-muted-foreground'}`}>
                {teamLimits.used} / {teamLimits.limit} members
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">Manage admins and dispatchers who can access the dashboard</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button 
                    className={`btn-primary ${atLimit ? 'opacity-50 cursor-not-allowed' : ''}`} 
                    data-testid="add-member-btn"
                    onClick={handleAddClick}
                    disabled={atLimit}
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Team Member
                    {atLimit && <AlertTriangle className="w-4 h-4 ml-2 text-yellow-400" />}
                  </Button>
                </span>
              </TooltipTrigger>
              {atLimit && (
                <TooltipContent className="bg-popover border-border text-popover-foreground">
                  <p>Team member limit reached ({teamLimits.used}/{teamLimits.limit}). Upgrade to add more.</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <DialogContent className="bg-background border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="text-foreground font-['Manrope']">
                {editingMember ? "Edit Team Member" : "Add Team Member"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-glass"
                  placeholder="Full name"
                  required
                  data-testid="member-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-glass"
                  placeholder="email@example.com"
                  required
                  data-testid="member-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Phone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-glass"
                  placeholder="+1 (555) 000-0000"
                  data-testid="member-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="input-glass" data-testid="member-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-[#FFCC00]" />
                        Admin (Full Access)
                      </div>
                    </SelectItem>
                    <SelectItem value="dispatcher">
                      <div className="flex items-center gap-2">
                        <Headphones className="w-4 h-4 text-purple-400" />
                        Dispatcher (Limited Access)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {formData.role === "admin" 
                    ? "Admins have full access to all features including settings and billing." 
                    : "Dispatchers can manage jobs, customers, and technicians but cannot access settings or billing."}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-theme">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-muted-foreground">
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" data-testid="member-submit-btn">
                  {editingMember ? "Update" : "Add"} Member
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Card className="glass-card border-[#0046BE]/30 bg-[#0046BE]/5">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-[#0046BE] mt-0.5" />
          <div>
            <p className="text-muted-foreground text-sm">
              <strong>Team</strong> members are admins and dispatchers who can access the full dashboard. 
              They count towards your plan limit.
            </p>
            <p className="text-muted-foreground text-xs mt-1">
              For field workers, use the <strong>Technicians</strong> page - technicians are unlimited on all plans.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Team Members Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading team members...</div>
      ) : members.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((member, index) => (
            <Card key={member.member_id} className="glass-card border-theme group" data-testid={`member-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-14 w-14 border-2 border-theme">
                      <AvatarFallback className="bg-[#0046BE] text-white text-lg">
                        {member.name.split(" ").map(n => n[0]).join("").toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold text-foreground">{member.name}</h3>
                      <Badge className={`${getRoleBadge(member.role)} border capitalize mt-1 flex items-center gap-1 w-fit`}>
                        {getRoleIcon(member.role)}
                        {member.role}
                      </Badge>
                    </div>
                  </div>
                  <Badge className={`${getStatusBadge(member.account_status || member.status)} border capitalize`}>
                    {member.account_status || member.status || "pending"}
                  </Badge>
                </div>

                <div className="space-y-2 mb-4">
                  <p className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Mail className="w-4 h-4" /> {member.email}
                  </p>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-theme">
                  <div className="text-sm text-muted-foreground">
                    {member.role === "admin" ? "Full dashboard access" : "Limited dashboard access"}
                  </div>
                  <div className="flex gap-1">
                    {(member.account_status !== "active") && (
                      <>
                        {member.email && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleSendInvite(member, "email")}
                            disabled={sendingInvite === member.member_id}
                            className="text-[#0046BE] hover:text-[#0046BE] hover:bg-[#0046BE]/10 h-8 w-8"
                            title={`Email invite to ${member.email}`}
                            data-testid={`invite-email-${index}`}
                          >
                            {sendingInvite === member.member_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        {member.phone && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleSendInvite(member, "sms")}
                            disabled={sendingInvite === member.member_id}
                            className="text-green-500 hover:text-green-400 hover:bg-green-500/10 h-8 w-8"
                            title={`SMS invite to ${member.phone}`}
                            data-testid={`invite-sms-${index}`}
                          >
                            {sendingInvite === member.member_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <MessageSquare className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(member)}
                      className="text-muted-foreground hover:text-white hover:bg-theme-hover h-8 w-8"
                      data-testid={`edit-member-${index}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(member.member_id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8"
                      data-testid={`delete-member-${member.member_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="glass-card border-theme">
          <CardContent className="text-center py-12">
            <UserCircle className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-muted-foreground">No team members yet</p>
            <p className="text-muted-foreground text-sm mb-4">Add admins or dispatchers to help manage your business</p>
            <Button 
              className={`mt-4 btn-primary ${atLimit ? 'opacity-50' : ''}`} 
              onClick={handleAddClick} 
              data-testid="empty-add-member-btn"
              disabled={atLimit}
            >
              Add Your First Team Member
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TeamPage;
